import { useState } from "react";
import { Download } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { evaluationApi, procPdfApi } from "../../api";
import { ProcError, ProcField, ProcForm, ProcGrid, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { useAsyncData, useMutation } from "../../hooks";

function calcTotal(s: { qualityScore?: number; deliveryScore?: number; pricingScore?: number; responsivenessScore?: number; complianceScore?: number }): number {
  return (s.qualityScore ?? 0) + (s.deliveryScore ?? 0) + (s.pricingScore ?? 0) + (s.responsivenessScore ?? 0) + (s.complianceScore ?? 0);
}

export default function EvaluationViewPage() {
  const { id } = useParams<{ id: string }>();
  const [downloading, setDownloading] = useState(false);

  const { data: ev, loading, error, reload } = useAsyncData(
    () => evaluationApi.get(Number(id)),
    [id],
  );

  const { mutate: doApprove, loading: approving, error: approveErr } = useMutation(() =>
    evaluationApi.approve(Number(id)).then(() => reload()),
  );
  const { mutate: doReject, loading: rejecting, error: rejectErr } = useMutation(() =>
    evaluationApi.reject(Number(id)).then(() => reload()),
  );

  async function downloadPdf() {
    if (!ev) return;
    setDownloading(true);
    try {
      const blob = await procPdfApi.evaluation(Number(id));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `evaluation-${ev.serialNo ?? id}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } finally { setDownloading(false); }
  }

  if (loading) return <ProcLoading />;
  if (error) return <ProcError message={error} />;
  if (!ev) return null;

  const actionErr = approveErr || rejectErr;
  const scores = ev.suppliers.map(calcTotal);
  const maxScore = Math.max(...scores, 0);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <Link to="/procurement/evaluations" className="text-sm text-emerald-700 hover:underline">&larr; Back to Evaluations</Link>
        <button onClick={downloadPdf} disabled={downloading}
          className="flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          <Download className="h-4 w-4" />
          {downloading ? "Downloading…" : "Download PDF"}
        </button>
      </div>

      <ProcForm formNo="PROC-FRM-03" title="Quotation Evaluation" serialNo={ev.serialNo} date={ev.date}>
        <ProcSection title="Details">
          <ProcGrid>
            <ProcField label="Linked RFQ" value={ev.rfqSerialNo ?? (ev.rfqId ? String(ev.rfqId) : "—")} />
            <ProcField label="Description" value={ev.description} span2 />
          </ProcGrid>
        </ProcSection>

        <ProcSection title="Supplier Scores">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Supplier</th>
                  <th className="px-3 py-2 text-right">Quoted (KES)</th>
                  <th className="px-3 py-2 text-left">Delivery</th>
                  <th className="px-3 py-2 text-right">Quality/30</th>
                  <th className="px-3 py-2 text-right">Delivery/25</th>
                  <th className="px-3 py-2 text-right">Pricing/20</th>
                  <th className="px-3 py-2 text-right">Response/15</th>
                  <th className="px-3 py-2 text-right">Comply/10</th>
                  <th className="px-3 py-2 text-right">Total/100</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {ev.suppliers.map((s, idx) => {
                  const total = calcTotal(s);
                  const isTop = total > 0 && total === maxScore && ev.suppliers.length > 1;
                  return (
                    <tr key={idx} className={isTop ? "bg-emerald-50" : ""}>
                      <td className="px-3 py-2 font-semibold text-slate-800">{s.supplierName}</td>
                      <td className="px-3 py-2 text-right">{s.amountQuoted.toLocaleString()}</td>
                      <td className="px-3 py-2 text-slate-600">{s.deliveryPeriod}</td>
                      <td className="px-3 py-2 text-right">{s.qualityScore ?? 0}</td>
                      <td className="px-3 py-2 text-right">{s.deliveryScore ?? 0}</td>
                      <td className="px-3 py-2 text-right">{s.pricingScore ?? 0}</td>
                      <td className="px-3 py-2 text-right">{s.responsivenessScore ?? 0}</td>
                      <td className="px-3 py-2 text-right">{s.complianceScore ?? 0}</td>
                      <td className={`px-3 py-2 text-right font-bold ${isTop ? "text-emerald-700" : "text-slate-700"}`}>
                        {total}{isTop && " ★"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </ProcSection>

        <ProcSection title="Recommendation">
          <ProcGrid>
            <ProcField label="Recommended Supplier" value={ev.recommendedSupplierName} />
            <ProcField label="Reason" value={ev.recommendationReason} span2 />
          </ProcGrid>
        </ProcSection>

        {(!ev.status || ev.status === "DRAFT") && (
          <ProcSection title="Actions">
            {actionErr && <ProcError message={actionErr} />}
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={() => doApprove()} disabled={approving}
                className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60">
                {approving ? "Approving…" : "Approve Evaluation"}
              </button>
              <button type="button" onClick={() => doReject()} disabled={rejecting}
                className="rounded-xl border border-red-300 px-5 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60">
                {rejecting ? "Rejecting…" : "Reject Evaluation"}
              </button>
            </div>
          </ProcSection>
        )}
      </ProcForm>
    </div>
  );
}
